/**
 * @file Display-layer helpers for airport/city search rows.
 *
 * Pure text transforms, kept out of `airport-search.jsx` (the API client) so
 * they stay independently testable. Nothing here rebuilds a `sub_label` from
 * its city/state/country parts — the API pre-formats that string, including
 * dropping missing pieces and shortening formal country names.
 */

/** Latin letters with a stroke/ligature that NFD cannot decompose. */
const STROKE = { ø: 'o', ł: 'l', đ: 'd', æ: 'ae', œ: 'oe', ß: 'ss', ð: 'd', þ: 'th' };

const WORD = /[\p{L}\p{N}]+/gu;
// `\p{M}` (any Unicode mark) rather than a ̀-ͯ range: it covers every
// diacritic NFD produces, and keeps this file free of invisible combining
// characters that editors mangle on save.
const COMBINING_MARKS = /\p{M}/gu;

/**
 * Case-, accent- and stroke-insensitive folding, mirroring the API's own
 * folding so `zurich` matches Zürich and `tromso` matches Tromsø.
 */
const fold = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(COMBINING_MARKS, '')
    .toLowerCase()
    .replace(/[øłđæœßðþ]/g, (char) => STROKE[char] || char);

/**
 * Display-only cleanup of a pre-formatted `sub_label`.
 *
 * Fixes two documented quirks (guide §10) without rebuilding the string:
 *   "New York, New York, United States"  → "New York, United States"
 *   "New York, New-York, United States"  → "New York, United States"
 *
 * Only *adjacent* duplicate segments collapse, so a legitimate repeat further
 * along survives. De-hyphenation is gated on `country === 'US'` because the
 * hyphens are a US-source-data artefact ("New-York", "New-Jersey") while place
 * names elsewhere are legitimately hyphenated (Baden-Württemberg).
 */
export const tidySubLabel = (row) => {
  const raw = row?.sub_label || '';
  if (!raw) return '';

  const isUS = row.country === 'US';
  const norm = (segment) => segment.replace(/-/g, ' ').toLowerCase();
  const out = [];

  raw
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)
    .forEach((part) => {
      const display = isUS ? part.replace(/-/g, ' ') : part;
      const prev = out[out.length - 1];
      if (prev && norm(prev) === norm(display)) return;
      out.push(display);
    });

  return out.join(', ');
};

/**
 * Split `text` into `{ text, hit }` runs for query highlighting, rendering
 * NOTHING rather than something wrong.
 *
 * The API is typo-tolerant (`chicgao`→CHI, `heathrwo`→LHR), so plain substring
 * highlighting finds no match on precisely the rows fuzzy matching produced —
 * and a naive per-character match would scatter marks like confetti. Two passes
 * then a floor:
 *   pass 1  token is a prefix of a word    "new york" → **New** **York**
 *                                          "new"      → **New**ark
 *   pass 2  spaceless token spans words    "newyork"  → **New York**
 *   floor   fewer than 2 marked chars      "chicgao"  → Chicago (unmarked)
 *
 * Always pass `resolvedQuery` from the hook, never the live input: the visible
 * rows belong to the resolved query, so highlighting against live text makes
 * marks flicker and desync mid-request.
 */
export const highlightRuns = (text, query) => {
  const src = String(text ?? '');
  if (!src) return [];
  const plain = [{ text: src, hit: false }];

  const tokens = [...new Set(fold(query).match(WORD) || [])].filter((token) => token.length >= 2);
  if (!tokens.length) return plain;

  const words = [...src.matchAll(WORD)].map((match) => ({
    start: match.index,
    end: match.index + match[0].length,
    folded: fold(match[0])
  }));
  if (!words.length) return plain;

  const marks = [];

  // Pass 1 — token is a prefix of some word.
  tokens.forEach((token) => {
    words.forEach((word) => {
      if (word.folded.startsWith(token)) {
        marks.push([word.start, Math.min(word.start + token.length, word.end)]);
      }
    });
  });

  // Pass 2 — one spaceless token spanning consecutive words ("newyork").
  if (!marks.length) {
    tokens.forEach((token) => {
      for (let i = 0; i < words.length; i += 1) {
        let acc = '';
        let j = i;
        while (j < words.length && token.startsWith(acc + words[j].folded)) {
          acc += words[j].folded;
          j += 1;
          if (acc === token) break;
        }
        if (acc === token && j - i > 1) {
          marks.push([words[i].start, words[j - 1].end]);
          return;
        }
      }
    });
  }

  if (!marks.length) return plain;

  marks.sort((a, b) => a[0] - b[0]);
  const merged = marks.reduce((acc, [start, end]) => {
    const last = acc[acc.length - 1];
    if (last && start <= last[1]) {
      last[1] = Math.max(last[1], end);
    } else {
      acc.push([start, end]);
    }
    return acc;
  }, []);

  // Bail out if the result would be confetti rather than a readable emphasis.
  if (merged.reduce((total, [start, end]) => total + (end - start), 0) < 2) return plain;

  const runs = [];
  let cursor = 0;
  merged.forEach(([start, end]) => {
    if (start > cursor) runs.push({ text: src.slice(cursor, start), hit: false });
    runs.push({ text: src.slice(start, end), hit: true });
    cursor = end;
  });
  if (cursor < src.length) runs.push({ text: src.slice(cursor), hit: false });

  return runs;
};

/**
 * Whether the query names this IATA code outright. Exact tokens only — a prefix
 * test would ring the JFK badge for a bare "j".
 */
export const isCodeHit = (code, query) => {
  const folded = fold(code);
  if (!folded) return false;
  const foldedQuery = fold(query);
  return foldedQuery.trim() === folded || (foldedQuery.match(WORD) || []).includes(folded);
};

export default tidySubLabel;
